/**
 * Settings → AI's explicit Save (slice 1j spec §3): the draft of Model
 * routing and Image model, the one bar over both cards, and the prompt
 * for leaving with a draft. What the bar says comes from `routing-draft.ts`.
 */
import { Button } from "@crew/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@crew/ui/components/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { Spinner } from "@crew/ui/components/spinner";
import { cn } from "@crew/ui/lib/utils";
import { Check, CircleAlert, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";
import { useWide } from "../../app/wide";
import type { BarView, SaveStepState } from "./routing-draft";
import type { RoutingDraft } from "./use-routing-draft";

function DraftIcon({ tone }: { tone: Extract<BarView, { kind: "draft" }>["tone"] }) {
  if (tone === "refused") return <CircleAlert className="size-4 shrink-0 text-[#E7A193]" />;
  if (tone === "conflict") return <RefreshCw className="size-4 shrink-0 text-[#D9A27E]" />;
  return <span className="mx-1 size-2 shrink-0 rounded-full bg-[#D9A27E]" />;
}

function StepMark({ state }: { state: SaveStepState }) {
  if (state === "done") return <Check className="size-3.5 shrink-0" />;
  if (state === "current")
    return <span className="mx-1 size-1.5 shrink-0 rounded-full bg-[#D9A27E]" />;
  return <span className="mx-1 size-1.5 shrink-0 rounded-full border border-current" />;
}

/** The dark bar itself: full width over the tab bar on a phone, floating under the cards on a desktop. */
function BarFrame({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div
      role="region"
      aria-label={label}
      data-save-bar
      className={cn(
        "fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 flex flex-col gap-3 rounded-t-2xl bg-foreground px-4 pt-4 pb-4 text-background shadow-[0_-8px_24px_rgb(31_42_68/0.18)]",
        "md:sticky md:inset-x-auto md:bottom-6 md:rounded-xl md:px-5 md:py-3 md:shadow-[0_12px_32px_rgb(31_42_68/0.22),0_2px_6px_rgb(31_42_68/0.12)]",
      )}
    >
      {children}
    </div>
  );
}

function SavingBar({ steps }: { steps: { label: string; state: SaveStepState }[] }) {
  return (
    <BarFrame label="Saving">
      <div role="status" className="flex flex-col gap-2">
        <p className="flex items-center gap-2 text-body font-medium">
          <Spinner className="size-4" />
          Saving
        </p>
        <ol className="flex flex-col gap-1.5 pl-6 text-meta md:flex-row md:flex-wrap md:gap-x-5">
          {steps.map((step) => (
            <li
              key={step.label}
              data-step={step.state}
              className={cn(
                "flex items-center gap-1.5",
                step.state === "current" ? "font-medium text-background" : "text-background/65",
              )}
            >
              <StepMark state={step.state} />
              {step.label}
            </li>
          ))}
        </ol>
        <p className="pl-6 text-meta text-background/72">
          New messages are queued until it's ready. You can leave this page.
        </p>
      </div>
    </BarFrame>
  );
}

function SavedToast() {
  return (
    <div
      role="status"
      data-save-toast
      className="fixed inset-x-3 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] z-40 flex items-center gap-3 rounded-xl bg-card px-4 py-3 text-body text-foreground shadow-[0_12px_32px_rgb(31_42_68/0.18)] ring-1 ring-border md:sticky md:inset-x-auto md:bottom-6"
    >
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-moss text-white">
        <Check className="size-3.5" />
      </span>
      Saved. Your crew uses the new routing from the next message.
    </div>
  );
}

/** Unsaved changes · Discard · Save changes, and its other words (§3.2, §3.6, §3.7). */
function DraftBar({
  view,
  pending,
  onDiscard,
  onSave,
}: {
  view: Extract<BarView, { kind: "draft" }>;
  pending: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  return (
    <BarFrame label="Unsaved changes">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        <div
          className="flex min-w-0 flex-1 flex-col gap-0.5"
          role="status"
          data-bar-tone={view.tone}
        >
          <p className="flex items-center gap-2 text-body font-medium">
            <DraftIcon tone={view.tone} />
            {view.title}
          </p>
          <p className="pl-6 text-meta text-background/72">{view.text}</p>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-2 md:flex md:shrink-0 md:items-center">
          <Button
            variant="ghost"
            onClick={onDiscard}
            disabled={pending}
            className="h-11 border border-background/25 text-background hover:bg-background/10 hover:text-background md:h-9 md:border-0"
          >
            Discard
          </Button>
          <Button
            onClick={onSave}
            disabled={!view.canSave || pending}
            className="h-11 bg-background font-semibold text-foreground hover:bg-background/90 disabled:bg-background/35 disabled:text-foreground/70 md:h-9"
          >
            {pending ? <Spinner className="size-4" /> : null}
            Save changes
          </Button>
        </div>
      </div>
    </BarFrame>
  );
}

/** The bar for whatever the draft is doing; nothing with no draft and no save. */
export function SaveBar({ draft }: { draft: RoutingDraft }) {
  const { view } = draft;
  if (view.kind === "saving") return <SavingBar steps={view.steps} />;
  if (view.kind === "saved") return <SavedToast />;
  if (view.kind === "none") return null;
  return (
    <DraftBar view={view} pending={false} onDiscard={draft.discard} onSave={() => draft.save()} />
  );
}

const LEAVE_TITLE = "Save your routing changes?";
const LEAVE_LOST = "You have unsaved changes. If you leave now, they're lost.";
const LEAVE_RUNS =
  "Saving restarts your crew workspace once current messages are answered. It carries on after you leave.";

function LeaveActions({
  wide,
  pending,
  onSave,
  onStay,
  onLeave,
}: {
  wide: boolean;
  pending: boolean;
  onSave: () => void;
  onStay: () => void;
  onLeave: () => void;
}) {
  const save = (
    <Button onClick={onSave} disabled={pending} className={wide ? "" : "h-11"}>
      {pending ? <Spinner className="size-4" /> : null}
      Save and leave
    </Button>
  );
  const stay = (
    <Button variant="outline" onClick={onStay} disabled={pending} className={wide ? "" : "h-11"}>
      Keep editing
    </Button>
  );
  const leave = (
    <Button
      variant="ghost"
      onClick={onLeave}
      disabled={pending}
      className="text-brick hover:text-brick"
    >
      Don't save
    </Button>
  );
  if (!wide)
    return (
      <div className="flex flex-col gap-2">
        {save}
        {stay}
        {leave}
      </div>
    );
  return (
    <div className="flex w-full items-center gap-2">
      <span className="mr-auto">{leave}</span>
      {stay}
      {save}
    </div>
  );
}

/**
 * Leaving Settings → AI with a draft (§3.8): the app's own navigation asks
 * here, a dialog on a desktop and a sheet on a phone; closing or reloading
 * the tab gets the browser's prompt.
 */
function LeaveGuard({ draft }: { draft: RoutingDraft }) {
  const wide = useWide();
  const [leaving, setLeaving] = useState(false);
  const { dirty } = draft;
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        dirty && currentLocation.pathname !== nextLocation.pathname,
      [dirty],
    ),
  );
  useEffect(() => {
    if (!draft.dirty) return;
    const ask = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [draft.dirty]);
  const blocked = blocker.state === "blocked";
  const stay = () => blocker.reset?.();
  const leave = () => blocker.proceed?.();
  const saveAndLeave = () => {
    setLeaving(true);
    draft.save({
      onSaved: leave,
      onFailed: () => {
        setLeaving(false);
        stay();
      },
    });
  };
  const change = (next: boolean) => {
    if (!next && !leaving) stay();
  };
  const actions = (
    <LeaveActions
      wide={wide}
      pending={leaving}
      onSave={saveAndLeave}
      onStay={stay}
      onLeave={leave}
    />
  );
  if (!wide) {
    return (
      <Drawer open={blocked} onOpenChange={change} showSwipeHandle>
        <DrawerContent className="pb-[env(safe-area-inset-bottom)]" data-leave-prompt>
          <DrawerHeader className="gap-2 px-5 pt-4 group-data-[swipe-axis=y]/drawer-popup:text-left">
            <DrawerTitle>{LEAVE_TITLE}</DrawerTitle>
            <DrawerDescription className="text-body text-foreground">
              {LEAVE_LOST}
            </DrawerDescription>
            <p className="text-meta text-muted-foreground">{LEAVE_RUNS}</p>
          </DrawerHeader>
          <DrawerFooter className="px-5 pt-5">{actions}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={blocked} onOpenChange={change}>
      <DialogContent className="sm:max-w-[440px]" showCloseButton={false} data-leave-prompt>
        <DialogHeader>
          <DialogTitle>{LEAVE_TITLE}</DialogTitle>
          <DialogDescription className="text-body text-foreground">{LEAVE_LOST}</DialogDescription>
          <p className="text-meta text-muted-foreground">{LEAVE_RUNS}</p>
        </DialogHeader>
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The leave prompt while there's a draft to lose, kept mounted through a
 * save (Save and leave clears the draft before it navigates), but asking
 * nothing once the draft is gone: a save carries on without the page.
 */
export function LeavePrompt({ draft }: { draft: RoutingDraft }) {
  return draft.dirty || draft.saving ? <LeaveGuard draft={draft} /> : null;
}
